// shared.jsx - Gedeelde componenten en helpers voor dashboard pagina's
import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Calendar,
  Clock,
  User,
  Users,
  Edit2,
  Save,
  Utensils,
  ShoppingCart,
  Check,
  CheckCircle,
  CheckSquare,
  Star,
  PartyPopper,
  ClipboardList,
  FileText,
  Brush,
  Loader2,
  MapPin,
  AlertTriangle,
  Trash2,
  Wifi,
  Copy,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Heart,
  Activity,
  Thermometer,
  Scale,
  Droplets,
  Minus,
  History,
  Phone,
  Mail,
  Home,
  Search,
  Users2,
  Sun,
  Moon,
  Shield,
  Edit,
  X,
  Sparkles,
  Pill,
  CalendarDays,
  ListTodo,
  MessageSquare,
  Briefcase,
  Dumbbell,
  Mic,
  MicOff,
  Send,
} from 'lucide-react';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Area, AreaChart } from 'recharts';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

export const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============================================
// PREMIUM PAGE WRAPPER - iPhone-stijl container
// ============================================
export const PageWrapper = ({ children, title, subtitle, onBack, gradient = 'from-slate-50 via-purple-50/30 to-pink-50/20', icon: Icon, iconGradient = 'from-violet-500 to-purple-600' }) => (
  <motion.div 
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    className={`min-h-screen bg-gradient-to-br ${gradient}`}
  >
    {/* Background Orbs */}
    <div className="fixed inset-0 overflow-hidden pointer-events-none">
      <div className="absolute top-20 -left-20 w-72 h-72 bg-purple-300/20 rounded-full blur-3xl" />
      <div className="absolute top-40 right-10 w-96 h-96 bg-pink-300/20 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-1/3 w-80 h-80 bg-violet-300/15 rounded-full blur-3xl" />
    </div>
    
    {/* Sticky Header */}
    <div className="sticky top-0 z-40 bg-white/70 backdrop-blur-xl border-b border-white/50 shadow-sm">
      <div className="max-w-6xl mx-auto px-4 py-3">
        <div className="flex items-center gap-4">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onBack}
            className="p-2.5 rounded-xl bg-white/80 hover:bg-white shadow-sm border border-slate-200/50 transition-all"
          >
            <ChevronLeft className="w-5 h-5 text-slate-700" />
          </motion.button>
          
          <div className="flex items-center gap-3 flex-1">
            {Icon && (
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${iconGradient} flex items-center justify-center shadow-lg`}>
                <Icon className="w-5 h-5 text-white" />
              </div>
            )}
            <div>
              <h1 className="text-lg font-bold text-slate-800">{title}</h1>
              {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
    
    {/* Content */}
    <div className="relative z-10 max-w-6xl mx-auto px-4 py-6">
      {children}
    </div>
  </motion.div>
);

// Premium Card Component
export const PremiumCard = ({ children, className = '', hover = true, padding = 'p-4' }) => (
  <motion.div
    whileHover={hover ? { y: -2, scale: 1.01 } : {}}
    className={`bg-white/80 backdrop-blur-sm rounded-2xl shadow-sm border border-white/60 ${padding} ${className} ${hover ? 'hover:shadow-md transition-all duration-300' : ''}`}
  >
    {children}
  </motion.div>
);

// Swipe-to-go-back wrapper component  
export const SwipeBackWrapper = ({ children, onBack }) => {
  const [startX, setStartX] = useState(0);
  const [currentX, setCurrentX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const handleTouchStart = (e) => {
    // Only start swipe if touch starts from left edge (within 30px)
    if (e.touches[0].clientX < 30) {
      setStartX(e.touches[0].clientX);
      setIsDragging(true);
    }
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const diff = e.touches[0].clientX - startX;
    if (diff > 0) {
      setCurrentX(diff);
    }
  };

  const handleTouchEnd = () => {
    if (currentX > 100) {
      onBack && onBack();
    }
    setCurrentX(0);
    setIsDragging(false);
  };

  return (
    <div
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{
        transform: `translateX(${currentX}px)`,
        transition: isDragging ? 'none' : 'transform 0.3s ease-out',
      }}
      className="min-h-screen"
    >
      {/* Swipe indicator on left edge */}
      <div 
        className="fixed left-0 top-1/2 -translate-y-1/2 w-1 h-20 bg-slate-300/30 rounded-r-full z-50 pointer-events-none"
        style={{ opacity: isDragging ? 1 : 0.3 }}
      />
      {children}
    </div>
  );
};

// Premium List Item
export const PremiumListItem = ({ children, onClick, className = '' }) => (
  <motion.div
    whileHover={{ x: 4 }}
    whileTap={{ scale: 0.98 }}
    onClick={onClick}
    className={`p-4 bg-white/60 hover:bg-white/90 rounded-xl border border-slate-100 cursor-pointer transition-all ${className}`}
  >
    {children}
  </motion.div>
);

// Premium Badge
export const PremiumBadge = ({ children, variant = 'default' }) => {
  const variants = {
    default: 'bg-slate-100 text-slate-700',
    success: 'bg-emerald-100 text-emerald-700',
    warning: 'bg-amber-100 text-amber-700',
    danger: 'bg-red-100 text-red-700',
    info: 'bg-blue-100 text-blue-700',
    purple: 'bg-purple-100 text-purple-700',
  };
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${variants[variant]}`}>
      {children}
    </span>
  );
};

// ============================================
// ROBUST API HELPER - Centrale error handling met retry
// ============================================
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const apiRequest = async (method, endpoint, data = null, options = {}, retries = 2) => {
  const token = localStorage.getItem('token');
  const config = {
    method,
    url: `${API_URL}${endpoint}`,
    headers: { 
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    timeout: 15000, // 15 seconden timeout
    ...options
  };
  
  if (data && (method === 'post' || method === 'put')) {
    config.data = data;
  }
  
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await axios(config);
      return { success: true, data: response.data };
    } catch (error) {
      // Don't retry on client errors (4xx)
      if (error.response?.status >= 400 && error.response?.status < 500) {
        const message = error.response?.data?.detail || 
                        error.response?.data?.message || 
                        error.message || 
                        'Er ging iets mis';
        
        if (error.response?.status === 401) {
          toast.error('Sessie verlopen - log opnieuw in');
          localStorage.removeItem('token');
          window.location.href = '/';
          return { success: false, error: 'Unauthorized' };
        }
        
        if (error.response?.status === 403) {
          toast.error('Geen toegang tot deze functie');
          return { success: false, error: 'Forbidden' };
        }
        
        if (error.response?.status === 404) {
          return { success: false, error: 'Niet gevonden' };
        }
        
        return { success: false, error: message };
      }
      
      // Retry on server errors (5xx) or network issues
      if (attempt < retries) {
        await sleep(1000 * (attempt + 1)); // Exponential backoff: 1s, 2s
        continue;
      }
      
      // Final attempt failed
      if (error.response?.status >= 500) {
        toast.error('Server tijdelijk niet beschikbaar');
        return { success: false, error: 'Server error' };
      }
      
      if (error.code === 'ECONNABORTED') {
        toast.error('Verbinding timeout - probeer het opnieuw');
        return { success: false, error: 'Timeout' };
      }
      
      toast.error('Netwerkfout - controleer je internetverbinding');
      return { success: false, error: error.message || 'Network error' };
    }
  }
};

// Simplified API methods
export const api = {
  get: (endpoint, retries = 2) => apiRequest('get', endpoint, null, {}, retries),
  post: (endpoint, data, retries = 1) => apiRequest('post', endpoint, data, {}, retries),
  put: (endpoint, data, retries = 1) => apiRequest('put', endpoint, data, {}, retries),
  delete: (endpoint, retries = 1) => apiRequest('delete', endpoint, null, {}, retries)
};

// Safe data access helper - voorkomt "Cannot read property of undefined" errors
export const safeGet = (obj, path, defaultValue = null) => {
  try {
    return path.split('.').reduce((o, k) => (o || {})[k], obj) ?? defaultValue;
  } catch {
    return defaultValue;
  }
};

// ============================================
// SHARED UI COMPONENTS
// ============================================

export const PageHeader = ({ title, subtitle, onBack, rightAction, icon: Icon, iconGradient = 'from-violet-500 to-purple-600' }) => (
  <header className="sticky top-0 z-40 bg-white/70 backdrop-blur-xl border-b border-white/50 shadow-sm" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
    <div className="px-4 py-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onBack}
            className="p-2.5 rounded-xl bg-white/80 hover:bg-white shadow-sm border border-slate-200/50 transition-all"
            data-testid="back-button"
          >
            <ChevronLeft className="w-5 h-5 text-slate-700" />
          </motion.button>
          
          <div className="flex items-center gap-3">
            {Icon && (
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${iconGradient} flex items-center justify-center shadow-lg`}>
                <Icon className="w-5 h-5 text-white" />
              </div>
            )}
            <div>
              <h1 className="text-lg font-bold text-slate-800">{title}</h1>
              {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
            </div>
          </div>
        </div>
        {rightAction}
      </div>
    </div>
  </header>
);

export const EmptyState = ({ icon: Icon, title, subtitle, action }) => (
  <motion.div 
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="flex flex-col items-center justify-center py-16 px-6"
  >
    <div className="relative mb-6">
      <div className="absolute inset-0 bg-gradient-to-br from-purple-400 to-pink-400 rounded-3xl blur-xl opacity-30" />
      <div className="relative w-24 h-24 rounded-3xl bg-gradient-to-br from-slate-100 to-slate-50 flex items-center justify-center shadow-lg border border-slate-200/50">
        <Icon className="w-12 h-12 text-slate-400" />
      </div>
    </div>
    <h3 className="text-xl font-bold text-slate-800 mb-2">{title}</h3>
    <p className="text-sm text-slate-500 text-center mb-6 max-w-xs">{subtitle}</p>
    {action}
  </motion.div>
);

export const LoadingSpinner = ({ color = 'purple' }) => (
  <div className="flex flex-col items-center justify-center py-16">
    <div className="relative">
      <div className="absolute inset-0 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full blur-xl opacity-30 animate-pulse" />
      <div className="relative w-16 h-16 rounded-full bg-white shadow-lg flex items-center justify-center">
        <Loader2 className={`w-8 h-8 animate-spin text-${color}-500`} />
      </div>
    </div>
    <p className="mt-4 text-sm text-slate-500 font-medium">Laden...</p>
  </div>
);

// Re-export commonly used components and icons
export {
  // React hooks
  useState,
  useEffect,
  useMemo,
  // Framer Motion
  motion,
  AnimatePresence,
  // Axios
  axios,
  // Lucide icons
  ChevronLeft,
  ChevronRight,
  Plus,
  Calendar,
  Clock,
  User,
  Users,
  Edit2,
  Save,
  Utensils,
  ShoppingCart,
  Check,
  CheckCircle,
  CheckSquare,
  Star,
  PartyPopper,
  ClipboardList,
  FileText,
  Brush,
  Loader2,
  MapPin,
  AlertTriangle,
  Trash2,
  Wifi,
  Copy,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Heart,
  Activity,
  Thermometer,
  Scale,
  Droplets,
  Minus,
  History,
  Phone,
  Mail,
  Home,
  Search,
  Users2,
  Sun,
  Moon,
  Shield,
  Edit,
  X,
  Sparkles,
  Pill,
  CalendarDays,
  ListTodo,
  MessageSquare,
  Briefcase,
  Dumbbell,
  Mic,
  MicOff,
  Send,
  // Sonner
  toast,
  // Recharts
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Area,
  AreaChart,
  // UI Components
  Button,
  Input,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
};
